export const MAX_DATASET_MESSAGES = 100;
export const MAX_DATASETS = 100;
export const MAX_USER_MESSAGE_CHARS = 5000;

const MESSAGE_SEPARATOR_REGEX = /^\s*-{3,}\s*$/m;

export const parsePastedMessages = (raw: string): string[] =>
  raw
    .split(MESSAGE_SEPARATOR_REGEX)
    .map((m) => m.trim())
    .filter(Boolean);

export const getScoreColor = (score: number): string => {
  if (score >= 4) return 'text-success';
  if (score >= 3) return 'text-warning';
  return 'text-destructive';
};

// Mirrors backend `SimulationResultErrorCode` (run_state.py).
export type ResultErrorCode =
  | 'message_quota_exceeded'
  | 'invalid_action_name'
  | 'provider_unavailable'
  | 'provider_rejected'
  | 'provider_misconfigured' // Quickchat's own provider credentials or model access
  | 'interrupted' // a dead worker left this message unfinished
  | 'run_cancelled'
  | 'run_failed' // the run crashed before this message was processed
  | 'dispatch_failed' // the run never reached the worker
  | 'redispatch_failed' // recovery could not hand the stalled run back to a worker
  | 'unknown';

// Exhaustive on purpose: TypeScript rejects a new code that ships without copy.
const RESULT_ERROR_COPY: Record<ResultErrorCode, string> = {
  message_quota_exceeded: 'result_error.message_quota_exceeded',
  invalid_action_name: 'result_error.invalid_action_name',
  provider_unavailable: 'result_error.provider_unavailable',
  provider_rejected: 'result_error.provider_rejected',
  provider_misconfigured: 'result_error.provider_misconfigured',
  interrupted: 'result_error.interrupted',
  run_cancelled: 'result_error.run_cancelled',
  run_failed: 'result_error.run_failed',
  dispatch_failed: 'result_error.dispatch_failed',
  redispatch_failed: 'result_error.redispatch_failed',
  unknown: 'result_error.unknown',
};

export const RESULT_ERROR_CODES = Object.keys(RESULT_ERROR_COPY) as ResultErrorCode[];

// Rows written before the classifier shipped still hold raw provider payloads. Membership
// is tested against the key list, not `in`, which would also match Object.prototype keys.
export const toResultErrorCode = (errorMessage: string): ResultErrorCode =>
  RESULT_ERROR_CODES.includes(errorMessage as ResultErrorCode)
    ? (errorMessage as ResultErrorCode)
    : 'unknown';

export const resultErrorText = (t: (key: string) => string, errorMessage: string): string =>
  t(RESULT_ERROR_COPY[toResultErrorCode(errorMessage)]);
